import { Injectable, inject, signal, computed, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs';
import { MenuService, MenuNode } from './menu';

/**
 * Single source of truth for the current page's display name.
 *
 * The title is derived from the logged-in user's menu: whichever menu node's
 * route matches the active URL wins, so the page heading and the top-bar title
 * always read exactly the same as the menu item the user clicked. No page hard-
 * codes its name — they all bind to `title()`, so renaming a menu item in the
 * backend renames the page everywhere automatically.
 */
@Injectable({ providedIn: 'root' })
export class PageTitleService {
  private router = inject(Router);
  private menu = inject(MenuService);
  private platformId = inject(PLATFORM_ID);

  private url = signal(this.router.url.split('?')[0]);

  constructor() {
    if (isPlatformBrowser(this.platformId)) {
      this.router.events
        .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
        .subscribe((e) => this.url.set(e.urlAfterRedirects.split('?')[0]));
    }
  }

  readonly title = computed(() => {
    const url = this.url();
    // Backend menu routes may or may not include a leading slash — normalise both
    // sides so a missing slash doesn't silently drop us to the URL fallback.
    const norm = (r?: string | null) => {
      const s = (r || '').trim();
      return s ? (s.startsWith('/') ? s : '/' + s) : '';
    };
    let found = '';
    const walk = (nodes: MenuNode[]) =>
      nodes.forEach((n) => {
        if (n.route && norm(n.route) === url) found = n.title;
        if (n.children?.length) walk(n.children);
      });
    walk(this.menu.myMenu());
    if (found) return found;
    // Fallback before the menu has loaded (or for routes not in the menu):
    // tidy the last URL segment, e.g. "/series-types" -> "Series Types".
    const segs = url.split('/').filter(Boolean);
    // A detail route ends in a record id ("/partners/1"), and "1" is a useless title. Step back
    // to the section it belongs to so those pages read "Partners" / "Clients" instead.
    while (segs.length > 1 && /^\d+$/.test(segs[segs.length - 1])) segs.pop();
    const seg = segs.pop() || 'dashboard';
    return seg.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  });
}
